/**
 * Название бренда, собранное из сотен крошечных валютных обозначений (EUR, USD, ₿, ¥ …), —
 * подпись главного экрана в духе референса «созвездие частиц» (просьба заказчика 2026-09-17).
 * Частицы циклично разлетаются и снова собираются в надпись.
 *
 * Только canvas 2D и requestAnimationFrame; во вкладке не на виду и под prefers-reduced-motion
 * анимация стоит (надпись остаётся собранной). Имя бренда приходит из конфига — в коде его нет.
 */
import { useEffect, useRef } from 'react';

/** Что рисуем вместо точек: тикеры и знаки валют. */
const GLYPHS = ['EUR', 'USD', 'GBP', 'JPY', 'AUD', 'CHF', 'CAD', 'NZD', 'BTC', 'ETH', '$', '€', '£', '¥', '₿', '₣'];
/** Белая надпись с хроматикой TikTok (циан/красный) и каплями премиум-градиента Telegram. */
const COLORS = ['#ffffff', '#ffffff', '#ffffff', '#ffffff', '#ffffff', '#25f4ee', '#fe2c55', '#8a5cff', '#3b7bff'];
/** Потолок частиц: каждая — fillText на кадр, на телефоне больше тысячи уже не тянет. */
const MAX_PARTICLES = 900;

const HOLD_MS = 3_200;
const FLY_MS = 1_900;
const SCATTER_HOLD_MS = 1_400;
const CYCLE_MS = HOLD_MS + FLY_MS + SCATTER_HOLD_MS + FLY_MS;

interface Particle {
  hx: number;
  hy: number;
  sx: number;
  sy: number;
  glyph: string;
  color: string;
  size: number;
  /** Сдвиг фазы: разлетаются и собираются не строем, а волной. */
  delay: number;
  drift: number;
}

const easeInOut = (t: number): number => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2);

/** Точки надписи: рисуем текст на скрытом холсте и берём заполненные пиксели по сетке. */
function sampleText(text: string, width: number, height: number, step: number): Array<[number, number]> {
  const off = document.createElement('canvas');
  off.width = width;
  off.height = height;
  const ctx = off.getContext('2d');
  if (!ctx) return [];
  let fontSize = Math.floor(height * 0.9);
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  do {
    ctx.font = `800 ${fontSize}px "Inter Variable", Inter, system-ui, sans-serif`;
    fontSize -= 4;
  } while (ctx.measureText(text).width > width * 0.97 && fontSize > 12);
  ctx.fillStyle = '#fff';
  ctx.fillText(text, width / 2, height / 2);
  const data = ctx.getImageData(0, 0, width, height).data;
  const points: Array<[number, number]> = [];
  for (let y = 0; y < height; y += step) {
    for (let x = 0; x < width; x += step) {
      // сетка дробная — пиксель берём по целым координатам, иначе индекс мимо буфера
      const px = Math.round(x);
      const py = Math.round(y);
      if ((data[(py * width + px) * 4 + 3] ?? 0) > 128) points.push([x + (Math.random() - 0.5) * 1.5, y + (Math.random() - 0.5) * 1.5]);
    }
  }
  return points;
}

export function BrandConstellation({ text, height = 104 }: { text: string; height?: number }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !text) return undefined;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    const reduceMotion = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    let particles: Particle[] = [];
    let width = 0;
    let frame = 0;
    let running = true;
    const started = performance.now();

    const build = (): void => {
      const dpr = Math.min(globalThis.devicePixelRatio || 1, 2);
      width = canvas.clientWidth;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      // плотность частиц — под ширину экрана: ~700 на телефоне
      // сетка плотная: буквы на ширине телефона невысокие, и точки должны их прорисовать
      let step = width < 360 ? 3.2 : 2.9;
      let points = sampleText(text.toUpperCase(), width, height, step);
      // слишком плотно — разрежаем сетку, чтобы кадр укладывался в бюджет телефона
      if (points.length > MAX_PARTICLES) {
        step *= Math.sqrt(points.length / MAX_PARTICLES);
        points = sampleText(text.toUpperCase(), width, height, step);
      }
      particles = points.map(([hx, hy]) => ({
        hx,
        hy,
        sx: Math.random() * width,
        sy: Math.random() * height,
        glyph: GLYPHS[Math.floor(Math.random() * GLYPHS.length)] ?? '$',
        color: COLORS[Math.floor(Math.random() * COLORS.length)] ?? '#ffffff',
        size: 1.9 + Math.random() * 0.7,
        delay: Math.random() * 0.35,
        drift: Math.random() * Math.PI * 2,
      }));
      // сортировка по размеру: смена шрифта у canvas дорогая, а так соседние частицы делят одну строку font
      particles.sort((a, b) => a.size - b.size);
    };

    const draw = (now: number): void => {
      if (!running) return;
      const t = (now - started) % CYCLE_MS;
      ctx.clearRect(0, 0, width, height);
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      let lastFont = '';

      for (const p of particles) {
        // фаза частицы: 0 — собрана, 1 — разлетелась
        let k = 0;
        if (!reduceMotion) {
          if (t < HOLD_MS) k = 0;
          else if (t < HOLD_MS + FLY_MS) k = easeInOut(Math.min(1, Math.max(0, (t - HOLD_MS) / FLY_MS - p.delay) / (1 - p.delay)));
          else if (t < HOLD_MS + FLY_MS + SCATTER_HOLD_MS) k = 1;
          else k = 1 - easeInOut(Math.min(1, Math.max(0, (t - HOLD_MS - FLY_MS - SCATTER_HOLD_MS) / FLY_MS - p.delay) / (1 - p.delay)));
        }
        // в разлёте частицы чуть дышат, чтобы облако жило
        const wobble = k * 6;
        const x = p.hx + (p.sx - p.hx) * k + Math.sin(now / 900 + p.drift) * wobble;
        const y = p.hy + (p.sy - p.hy) * k + Math.cos(now / 1100 + p.drift) * wobble;
        ctx.globalAlpha = 0.55 + 0.45 * (1 - k) * 0.8 + 0.1 * Math.sin(now / 500 + p.drift);
        ctx.fillStyle = p.color;
        if (k < 0.08) {
          // в собранном виде — точки: квадратик в разы дешевле текста
          ctx.fillRect(x - 1, y - 1, 2.2, 2.2);
          continue;
        }
        // в разлёте — читаемые тикеры; размер округляем, шрифт ставим только когда он сменился
        const font = `600 ${Math.round((p.size + k * 4.5) * 2) / 2}px "Inter Variable", Inter, system-ui, sans-serif`;
        if (font !== lastFont) {
          ctx.font = font;
          lastFont = font;
        }
        ctx.fillText(p.glyph, x, y);
      }
      ctx.globalAlpha = 1;
      frame = requestAnimationFrame(draw);
    };

    build();
    // до загрузки шрифта надпись мерится запасным — пересобираем, когда Inter готов
    void document.fonts?.ready.then(() => build());
    frame = requestAnimationFrame(draw);

    const onVisibility = (): void => {
      const visible = document.visibilityState !== 'hidden';
      // без проверки running при возврате во вкладку запускался второй цикл кадров поверх первого
      if (visible && !running) {
        running = true;
        frame = requestAnimationFrame(draw);
      } else if (!visible && running) {
        running = false;
        cancelAnimationFrame(frame);
      }
    };
    // ширина меняется на целые пиксели редко — пересобираем только при реальном изменении,
    // иначе Telegram при раскрытии окна дёргает ResizeObserver несколько раз подряд
    const observer = new ResizeObserver(() => {
      if (canvas.clientWidth !== width) build();
    });
    observer.observe(canvas);
    // ушли с экрана (прокрутка) — кадры не рисуем
    const onScreen = new IntersectionObserver(([entry]) => {
      const visible = (entry?.isIntersecting ?? true) && document.visibilityState !== 'hidden';
      if (visible && !running) {
        running = true;
        frame = requestAnimationFrame(draw);
      } else if (!visible && running) {
        running = false;
        cancelAnimationFrame(frame);
      }
    });
    onScreen.observe(canvas);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      running = false;
      cancelAnimationFrame(frame);
      observer.disconnect();
      onScreen.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [text, height]);

  return <canvas ref={canvasRef} className="brand-constellation" style={{ height }} aria-label={text} role="img" />;
}
